import {
  getFirstPOAStartDate,
  getGroupData,
  getSelectedPeriod,
  isSubmissionOverdue,
} from "../../services/GroupContext";
import { processDateJS } from "../../utils/dateProcessor";

const defaultReturnStatus = {
  isFinalised: false,
  withoutReportingCompany: false,
  type: "",
  isSubmitted: false,
  submittedDate: undefined,
  reviewConfirmed: false,
  reviewedBy: "",
  isRevised: false,
  revisedNotes: [],
};

const validReturnTypes = {
  full: "Full",
  abbreviated: "Abbreviated",
  noReportingCompany: "No reporting company",
};

const headerSettings = [
  {
    title: "Period of Account Start Date",
    required: false,
    tooltipText: "poaStart",
  },
  {
    title: "Period of Account End Date",
    required: false,
    tooltipText: "poaEnd",
  },
  {
    title: "Return Status",
    required: false,
    tooltipText: "returnStatus",
  },
  {
    title: "Fixed / Group Ratio",
    required: false,
    tooltipText: "fixedGroupRatio",
  },
  {
    title: "Disallowance / (Reactivation)",
    required: false,
    tooltipText: "disallowanceOrReactivation",
  },
  {
    title: "Excess Debt Cap Generated",
    required: false,
    tooltipText: "excessDeptCap",
  },
  {
    title: "Unused Allowance generated/(utilised)",
    required: false,
    tooltipText: "unusedAllowanceGeneratedOrUtilised",
  },
];

const formatReturnStatus = (period) => {
  const selectedPoA = period ?? getSelectedPeriod() ?? {};
  const { return_status: returnStatus } = selectedPoA;

  if (!returnStatus) return "";

  let returnStatusDesc = [];
  const { isFinalised, isSubmitted, isRevised, type } = returnStatus ?? {};
  const hasActiveReportingCompany = Boolean(selectedPoA?.reporting_company);
  const showSubmissionOverdue = !isRevised && isSubmissionOverdue(selectedPoA);

  if (isFinalised) {
    returnStatusDesc.push(`Finalised ${type}`);

    if (isSubmitted) returnStatusDesc.push("Submitted");
    else if (showSubmissionOverdue) returnStatusDesc.push("Submission overdue");
    else returnStatusDesc.push("Not Submitted");
  } else {
    returnStatusDesc.push("Draft");

    if (showSubmissionOverdue) returnStatusDesc.push("Submission overdue");
    else if (!hasActiveReportingCompany)
      returnStatusDesc.push("No reporting company");
  }

  returnStatusDesc = returnStatusDesc.join(" - ");
  if (isRevised) returnStatusDesc = "Revised " + returnStatusDesc;

  return returnStatusDesc;
};

const datesMatchGroupConfig = (periodStart, periodEnd) => {
  const { model_first_period_start, model_first_period_end } =
    getGroupData() ?? {};

  const startDatesMatch = processDateJS(periodStart)?.isSame(
    processDateJS(model_first_period_start),
  );

  const endDatesMatch = processDateJS(periodEnd)?.isSame(
    processDateJS(model_first_period_end),
  );

  return startDatesMatch && endDatesMatch;
};

const isInvalidDate = (value) => {
  if (!value) return true;

  const date = new Date(value);
  return Number.isNaN(date.getTime());
};

const getDefaultEnd = (periodStart) => {
  const defaultDate = new Date(periodStart);
  defaultDate?.setFullYear(defaultDate?.getFullYear() + 1);
  defaultDate?.setDate(defaultDate?.getDate() - 1);
  return defaultDate;
};

const getNextDay = (date) => {
  const nextDay = new Date(date);
  nextDay?.setDate(nextDay?.getDate() + 1);
  return nextDay;
};

const getDatesErrorMessage = (index, dateType) => {
  if (index === 0) {
    return (
      <p className="error-message">
        Please ensure the {dateType} matches the first period of account entered
        in the Group Config page
      </p>
    );
  } else {
    return "Please enter a valid date";
  }
};

const normalizePeriodsForUI = (periodsOfAccount) => {
  const { model_first_period_start, model_first_period_end } =
    getGroupData() ?? {};
  // When data is loaded back in from the DB, it will be a string.
  // Need to parse it as a Date & fill any missing fields with defaults
  const normalizedPeriods = periodsOfAccount?.map((poa) => {
    const {
      period_start,
      period_end,
      return_status,
      apply_group_ratio_election,
      group_disallowance,
      group_interest_reactivation_available,
      group_excess_debt_cap,
      group_unused_allowance,
      unused_allowance,
    } = poa ?? {};

    return {
      ...poa,
      period_start: period_start
        ? new Date(period_start)
        : model_first_period_start,
      period_end: period_end ? new Date(period_end) : model_first_period_end,
      return_status: return_status ? return_status : { ...defaultReturnStatus },
      ratio_type: [undefined, null].includes(apply_group_ratio_election)
        ? "Not Selected"
        : apply_group_ratio_election
          ? "Group Ratio"
          : "Fixed Ratio",
      disallowance_reactivation:
        group_disallowance > 0
          ? group_disallowance
          : group_interest_reactivation_available * -1,
      group_excess_debt_cap: group_excess_debt_cap ?? "",
      unused_interest_allowance: group_unused_allowance ?? "",
      unused_allowance: unused_allowance ?? "",
    };
  });

  return normalizedPeriods;
};

const getCompanyIdsByJoinDateRule = (companies, joinDateRule) => {
  return companies
    .filter((company) => joinDateRule(company?.date_join))
    .map((company) => company?.company_ID);
};

const updateCreatedCompanyIdsList = (groupData) => {
  const { periods_of_account: periods, companies } = groupData ?? {};
  const firstPoAStart = processDateJS(getFirstPOAStartDate());
  const lastPoAEnd = processDateJS(periods[periods.length - 1].period_end);

  // add each company to the PoA in which its date_join lies
  periods?.forEach((period, index) => {
    const period_start = processDateJS(period?.period_start);
    const period_end = processDateJS(period?.period_end);

    periods[index].created_company_ids = getCompanyIdsByJoinDateRule(
      companies,
      (dateJoin) =>
        dateJoin &&
        processDateJS(dateJoin)?.isBetween(
          period_start,
          period_end,
          null,
          "[]",
        ),
    );
  });

  // get companies that have joined before the first PoA
  const earlyOrNoJoinCompanyIds = getCompanyIdsByJoinDateRule(
    companies,
    (dateJoin) => !dateJoin || processDateJS(dateJoin)?.isBefore(firstPoAStart),
  );

  // get companies that have joined after the last PoA
  const afterLastPoACompanyIds = getCompanyIdsByJoinDateRule(
    companies,
    (dateJoin) => dateJoin && processDateJS(dateJoin)?.isAfter(lastPoAEnd),
  );

  periods[0].created_company_ids = [
    ...periods[0].created_company_ids,
    ...earlyOrNoJoinCompanyIds,
  ];
  periods[periods.length - 1].created_company_ids = [
    ...periods[periods.length - 1].created_company_ids,
    ...afterLastPoACompanyIds,
  ];

  return { ...groupData, periods_of_account: periods };
};

const getSelectDisabledFlags = (periodOfAccounts) => {
  const selectionDisabledFlags = [false];

  for (let i = 1; i < periodOfAccounts.length; i++) {
    const prevPoA = periodOfAccounts[i - 1];
    const {
      return_status: { isFinalised },
      lastCompletedPoACalcs,
    } = prevPoA ?? {};

    if (!isFinalised && lastCompletedPoACalcs !== "editcfd") {
      const numOfDisabledPoAs = periodOfAccounts.length - i;

      selectionDisabledFlags.push(...new Array(numOfDisabledPoAs).fill(true));
      break;
    }

    selectionDisabledFlags.push(false);
  }

  return selectionDisabledFlags;
};

const getOldSavedPoAs = (poas) => {
  return new Map(
    poas
      .filter((poa) => poa?.id)
      .map((poa) => [
        poa.id,
        { period_start: poa.period_start, period_end: poa.period_end },
      ]),
  );
};

const getPeriodsSplit = (data, index) => {
  const { periods_of_account } = data
    ? { periods_of_account: data }
    : getGroupData();

  const periodsToSkip = periods_of_account.slice(0, index);
  const periodsToReopen = periods_of_account.slice(index);

  return { periodsToSkip, periodsToReopen };
};

const getNewReturnStatus = (period) => {
  let { isSubmitted, isRevised, isRevisionCharged, revisedNotes } =
    period.return_status ?? {};

  revisedNotes = revisedNotes ?? [];
  revisedNotes = [].concat(revisedNotes);
  revisedNotes.push("");

  return {
    isRevised: isSubmitted || isRevised,
    isFinalised: false,
    isSubmitted: false,
    reviewConfirmed: false,
    reviewedBy: "",
    type: "",
    withoutReportingCompany: false,
    revisedNotes: revisedNotes,
    code: "",
    timestamp: "",
    nature_of_revision: "",
    submission_reference: "",
    submittedDate: undefined,
    isRevisionCharged: isSubmitted ? false : Boolean(isRevisionCharged),
  };
};

export {
  datesMatchGroupConfig,
  defaultReturnStatus,
  formatReturnStatus,
  getDatesErrorMessage,
  getDefaultEnd,
  getNewReturnStatus,
  getNextDay,
  getOldSavedPoAs,
  getPeriodsSplit,
  getSelectDisabledFlags,
  headerSettings,
  isInvalidDate,
  normalizePeriodsForUI,
  updateCreatedCompanyIdsList,
  validReturnTypes,
};
